import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { BreadCrumbs, Pagination } from "../../components";
import { TableHeader, TableRow } from "../../components/TableComponents/TableComponents";
import { BreadCrumbsType } from "../../components/BreadCrumbs/BreadCrumbs";
import { headersType } from "../../models";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useDispatch } from "react-redux";
import { User } from "../../models/user.model";
import { getPatientDocuments } from "../../redux/slices/PatientSlice";
import { Link } from "react-router-dom";
import moment from "moment";
import { Document } from "../../models/document.model";

export const headers: headersType[] = [
  {
    title: "Request #",
    extraClasses: "w-full lg:w-240 pl-0",
    sortKey: "requestNumber",
    isClickable: true,
  },
  {
    title: "File Name",
    extraClasses: "w-full hidden lg:block break-all",
    sortKey: "fileName",
    isClickable: false,
  },
  {
    title: "Provider",
    extraClasses: "w-160 hidden lg:block",
    sortKey: "provider",
    isClickable: false,
  },
  {
    title: "Date Received",
    extraClasses: "w-160 hidden lg:block",
    sortKey: "dateReceived",
    isClickable: false,
  },
  {
    title: "",
    extraClasses: "w-56 hidden lg:block z-50 pr-0",
    sortKey: "",
    isClickable: false,
  },
];

const PatientDocuments = () => {
  const dispatch = useDispatch<AppDispatch>();
  const currentUser = useSelector((state: RootState) => state.currentUser.currentUser);
  const { documents, sortDocuments } = useSelector((state: RootState) => state.patient);
  const [extraClasses] = useState(headers.map(head => head.extraClasses));
  const [sortMethods] = useState<boolean[]>(headers.map(() => false));
  const [activeSortIndex] = useState<number>(-1);

  const breadcrumbs: BreadCrumbsType[] = [
    {
      title: "Documents",
      key: "#",
    },
  ];

  const handleSort = (index: number) => {};

  const headerIconAsc = <p> ↑</p>;
  const headerIconDesc = <p> ↓</p>;
  const sortIcons = sortMethods.map((item, index) => (activeSortIndex === index ? (item ? headerIconAsc : headerIconDesc) : null));

  const download = (doc: Document) => {
    if (doc.url) {
      fetch(doc.url)
        .then(response => {
          return response.blob();
        })
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `documents-${doc.requestNumber}`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
        })
        .catch(_ => {});
    }
  };

  const onPageChange = (e: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    if (currentUser) init(currentUser, newPage, sortDocuments.rowsPerPage);
  };

  const onRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (currentUser) init(currentUser, 0, parseInt(event.target.value, 10));
  };

  const init = async (currentUser: User, page = 0, size = 10) => {
    if (currentUser && currentUser?.offices && currentUser?.offices?.length && currentUser?.offices[0]?.officeId)
      await dispatch(
        getPatientDocuments({
          officeId: currentUser.offices[0].officeId,
          userId: currentUser.userId,
          page,
          size,
          sort: "createdDate,desc",
        })
      );
  };

  useEffect(() => {
    if (currentUser && !documents.length) init(currentUser);
  }, [currentUser]);

  return (
    <>
      <main>
        <div className="container mx-auto p-4 md:p-8 min-h-[80vh]">
          <BreadCrumbs items={breadcrumbs} />
          <div className="flex justify-between items-center mb-6">
            <h2 className="heading uppercase">
              Hi {currentUser?.firstName ?? ""} {currentUser?.lastName ?? ""}
            </h2>
          </div>
          <p className="text-base mb-5 font-medium">You will find all your current and past documents below.</p>
          {documents.length ? (
            <div className="block">
              <div className="flex justify-between items-center border-b border-docnote-blue-light border-opacity-50">
                {headers.map((head, index) => (
                  <TableHeader
                    title={head.title}
                    index={index}
                    key={index}
                    sortIcon={sortIcons[index]}
                    handleSort={handleSort}
                    extraClassesHeader={extraClasses[index]}
                    isClickable={head.isClickable}
                  />
                ))}
              </div>

              {documents?.map((item, index) => {
                const sortedData = {
                  request: (
                    <Link to={`/patients/${currentUser?.userId}/requests/${item.requestId}`} className="flex py-3">
                      {item.requestNumber}
                    </Link>
                  ),
                  service: (
                    <Link to={`/patients/${currentUser?.userId}/requests/${item.requestId}`} className="flex py-3">
                      {item.originalFilename}
                    </Link>
                  ),
                  provider: (
                    <Link to={`/patients/${currentUser?.userId}/requests/${item.requestId}`} className="flex py-3">{`${
                      item?.provider?.firstName ?? ""
                    } ${item?.provider?.lastName ?? ""}`}</Link>
                  ),
                  date: (
                    <Link to={`/patients/${currentUser?.userId}/requests/${item.requestId}`} className="flex py-3">
                      {moment(item?.createdDate).format("ll")}
                    </Link>
                  ),
                  comment: item?.url ? (
                    <div className="flex gap-2 z-[10000]">
                      <Link to={`${item.url}`} target="_blank">
                        <RemoveRedEyeOutlinedIcon className="text-docsigna-orange" />
                      </Link>
                      <div onClick={() => download(item)}>
                        <FileDownloadOutlinedIcon className="text-docsigna-orange" />
                      </div>
                    </div>
                  ) : null,
                };
                return (
                  <TableRow
                    data={Object.values(sortedData)}
                    key={`${index}-${item.requestNumber}`}
                    handleCloseTooltip={() => {}}
                    handleOpenTooltip={() => {}}
                    tooltipVisible={false}
                    extraClassesRow={extraClasses}
                    onRowClick={() => undefined}
                  />
                );
              })}
              <Pagination
                colSpan={10}
                count={sortDocuments.totalElements ?? 0}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                page={sortDocuments.pageNumber}
                rowsPerPage={sortDocuments.rowsPerPage}
                // rowsPerPageOptions={[10, 25, 50, 100].filter(i => userRequest.data && i < userRequest.data?.totalElements)}
              />
            </div>
          ) : (
            <p className="font-semibold">You have no documents at this time</p>
          )}

          <div></div>
        </div>
      </main>
    </>
  );
};

export default PatientDocuments;
